import React, { useState } from "react";

const SeatSelectionDialog = ({ isOpen, onClose, flightData }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedSeats, setSelectedSeats] = useState({});
  const [failedImages, setFailedImages] = useState(new Set());

  if (!isOpen) return null;

  // Convert FlightResults data structure to seat selection format
  const convertFlightData = (flight) => {
    if (!flight || !flight.segments) {
      // Fallback sample data
      return [
        {
          id: 1,
          route: "EWR to PHX",
          flightNumber: "AA 1234",
          date: "Saturday, August 23, 2025",
          aircraft: "Embraer 175",
          flightCount: "Flight 1 of 2"
        },
        {
          id: 2,
          route: "PHX to SBA", 
          flightNumber: "AA 3846",
          date: "Saturday, August 23, 2025",
          aircraft: "Embraer 175",
          flightCount: "Flight 2 of 2"
        }
      ];
    }

    // Convert segments to individual flights for seat selection
    return flight.segments.map((segment, index) => ({
      id: index + 1,
      route: segment.route,
      flightNumber: segment.flight,
      date: "Saturday, August 23, 2025", // Use actual date if available
      aircraft: segment.aircraft || "Embraer 175",
      flightCount: `Flight ${index + 1} of ${flight.segments.length}`
    }));
  };

  const flights = convertFlightData(flightData);

  // Authentic AA seat map data structure based on provided HTML
  const seatMap = {
    aircraft: "Embraer 175",
    travelTime: "1h 30m",
    sections: [
      {
        name: "First Class",
        rows: [
          { number: 1, seats: ["1A", "1B", "", "1D", "1F"], seatTypes: {"1A": "preferred window", "1B": "available", "1D": "unavailable", "1F": "unavailable"} },
          { number: 2, seats: ["2A", "2B", "", "2D", "2F"], seatTypes: {"2A": "unavailable", "2B": "available", "2D": "available aisle", "2F": "available window"} },
          { number: 3, seats: ["3A", "3B", "", "3D", "3F"], seatTypes: {"3A": "unavailable", "3B": "unavailable", "3D": "unavailable", "3F": "unavailable"} },
          { number: 4, seats: ["4A", "4B", "", "4D", "4F"], seatTypes: {"4A": "unavailable", "4B": "available", "4D": "available aisle", "4F": "preferred window"} }
        ]
      },
      {
        name: "Main Cabin",
        rows: [
          { number: 13, seats: ["13A", "13C", "", "13D", "13F"], seatTypes: {"13A": "preferred window wing", "13C": "unavailable", "13D": "unavailable", "13F": "unavailable"} },
          { number: 14, seats: ["14A", "14C", "", "14D", "14F"], seatTypes: {"14A": "preferred window wing", "14C": "preferred aisle armrest wing", "14D": "preferred aisle armrest wing", "14F": "preferred window wing"} },
          { number: 15, seats: ["15A", "15C", "", "15D", "15F"], seatTypes: {"15A": "unavailable", "15C": "unavailable", "15D": "unavailable", "15F": "available window wing"} },
          { number: 16, seats: ["16A", "16C", "", "16D", "16F"], seatTypes: {"16A": "preferred window", "16C": "unavailable", "16D": "unavailable", "16F": "available window"} },
          { number: 17, seats: ["17A", "17C", "", "17D", "17F"], seatTypes: {"17A": "mce window", "17C": "mce aisle armrest", "17D": "mce aisle armrest", "17F": "mce window"} },
          { number: 18, seats: ["18A", "18C", "", "18D", "18F"], seatTypes: {"18A": "mce window", "18C": "mce aisle armrest", "18D": "mce aisle armrest", "18F": "mce window"} },
          { number: 19, seats: ["19A", "19C", "", "19D", "19F"], seatTypes: {"19A": "available window", "19C": "available aisle armrest", "19D": "unavailable", "19F": "unavailable"} },
          { number: 20, seats: ["20A", "20C", "", "20D", "20F"], seatTypes: {"20A": "unavailable", "20C": "available aisle armrest", "20D": "preferred aisle armrest", "20F": "available window"} },
          { number: 21, seats: ["21A", "21C", "", "21D", "21F"], seatTypes: {"21A": "preferred window", "21C": "available aisle armrest", "21D": "unavailable", "21F": "available window"} },
          { number: 22, seats: ["22A", "22C", "", "22D", "22F"], seatTypes: {"22A": "available window", "22C": "available aisle armrest", "22D": "available aisle armrest", "22F": "unavailable"} },
          { number: 23, seats: ["23A", "23C", "", "23D", "23F"], seatTypes: {"23A": "unavailable", "23C": "unavailable", "23D": "unavailable", "23F": "unavailable"} }
        ]
      }
    ]
  };

  // Get seat info from seat map data
  const getSeatInfo = (seatId) => {
    for (const section of seatMap.sections) {
      for (const row of section.rows) {
        if (row.seatTypes && row.seatTypes[seatId]) {
          return {
            type: row.seatTypes[seatId],
            section: section.name,
            isMCE: section.isMCE || false
          };
        }
      }
    }
    return { type: "available", section: "Main Cabin", isMCE: false };
  };

  // Define seats that should show the status member icon (these will be preferred seats)
  const statusMemberSeats = ["1A", "4F", "16A", "20D", "21A"];
  
  const getSeatStatus = (seatId) => {
    const seatInfo = getSeatInfo(seatId);
    return seatInfo.type.split(' ')[0]; // Get the primary status (available, unavailable, mce, preferred)
  };
  
  const shouldShowStatusIcon = (seatId) => {
    return statusMemberSeats.includes(seatId);
  };

  // Remove seat selection functionality - make it view-only
  const handleSeatClick = (seatId) => {
    // Seats are now view-only, no selection functionality
    return;
  };

  const handleImageError = (imageSrc) => {
    setFailedImages(prev => new Set(prev).add(imageSrc));
  };

  const getSeatImage = (seatId) => {
    const status = getSeatStatus(seatId);
    
    // If this is a preferred seat that should show the status member icon
    if (shouldShowStatusIcon(seatId) && status === "preferred") {
      return "https://www.aa.com/content/images/seatMap/seats/seat-mpp-P-qblock@2x.png";
    }
    
    switch (status) {
      case "available":
        return "https://www.aa.com/content/images/seatMap/seats/seat-available@2x.png";
      case "unavailable":
        return "https://www.aa.com/content/images/seatMap/seats/seat-unavailable@2x.png";
      case "mce":
        return "https://www.aa.com/content/images/seatMap/seats/seat-mpp-L@2x.png";
      case "preferred":
        return "https://www.aa.com/content/images/seatMap/seats/seat-mpp-P@2x.png";
      default:
        return "https://www.aa.com/content/images/seatMap/seats/seat-available@2x.png";
    }
  };

  const getFallbackSeatStyles = (seatId) => {
    let styles = "w-full h-full rounded border-2 flex items-center justify-center text-xs font-medium ";
    
    const status = getSeatStatus(seatId);
    
    // If this is a preferred seat that should show the status member icon
    if (shouldShowStatusIcon(seatId) && status === "preferred") {
      styles += "bg-yellow-100 border-yellow-300 text-yellow-800 ";
      return styles;
    }
    
    switch (status) {
      case "available":
        styles += "bg-white border-gray-300 text-gray-800 ";
        break;
      case "unavailable":
        styles += "bg-gray-300 border-gray-400 text-gray-500 ";
        break;
      case "mce":
        styles += "bg-green-100 border-green-300 text-green-800 ";
        break;
      case "preferred":
        styles += "bg-yellow-100 border-yellow-300 text-yellow-800 ";
        break;
      default:
        styles += "bg-white border-gray-300 text-gray-800 ";
    }
    
    return styles;
  };

  const getSeatClasses = (seatId) => {
    const status = getSeatStatus(seatId);
    let classes = "w-6 h-6 cursor-pointer relative flex items-center justify-center ";
    
    if (status === "unavailable") {
      classes += "cursor-not-allowed ";
    } else {
      classes += "hover:scale-110 transition-transform ";
    }
    
    return classes;
  };

  const currentFlight = flights[activeTab];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white overflow-hidden rounded-lg shadow-xl flex flex-col"
        style={{
          width: '760px',
          maxWidth: '760px',
          maxHeight: '90vh',
          fontFamily: 'Arial, sans-serif',
          fontSize: '15px',
          color: '#36495a',
          fontWeight: '400',
          lineHeight: '1.4'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <h4 className="text-[32px] text-aa-blue">Seats (view only)</h4>
          <button
            onClick={onClose}
            className="w-8 h-8 border-2 border-aa-blue text-aa-blue rounded-full flex items-center justify-center hover:border-aa-dark-blue hover:text-aa-dark-blue transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs Navigation bar*/}
        <div className="mx-6 border-b border-gray-200">
          <div className="flex">
            {flights.map((flight, index) => (
              <button
                key={flight.id}
                onClick={() => setActiveTab(index)}
                className={`flex-1 px-4 py-4 text-center transition-colors relative hover:underline ${
                  activeTab === index
                    ? "text-aa-dark-blue bg-white border-t-2 border-l-2 border-r-2 border-aa-dark-blue -mb-px"
                    : "text-aa-blue hover:text-aa-blue hover:bg-gray-50 border-b-3 border-b-transparent"
                }`}
              >
                <div className="font-medium">{flight.route}</div>
                <div className={`text-sm ${
                  activeTab === index ? "text-aa-dark-blue" : "text-aa-blue"
                }`}>{flight.flightCount}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="mat-tab-body-wrapper flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
          {currentFlight && (
            <div className="mat-tab-body-content p-6">
              {/* Flight Route Header */}
              {/* <h2 className="text-lg font-medium text-gray-800 mb-4">Phoenix, AZ to Santa Barbara, CA</h2> */}
              
              {/* Flight Information */}
              <div className="mb-6">
                <ul className="flex items-center space-x-4 text-sm text-gray-600">
                  <li>AA 3846</li>
                  <li>{seatMap.aircraft}</li>
                  <li>Travel time: {seatMap.travelTime}</li>
                </ul>
              </div>

              {/* Seat Legend */}
              <div className="seat-legend mb-6 bg-slate-200 p-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center">
                    <img src="https://www.aa.com/content/images/seatMap/seats/seat-mpp-L@2x.png" alt="Main Cabin Extra" className="w-6 h-4 object-contain mr-2" />
                    <span className="text-sm text-gray-700">Main Cabin Extra ($)</span>
                  </div>
                  <div className="flex items-center">
                    <img src="https://www.aa.com/content/images/seatMap/seats/seat-mpp-P@2x.png" alt="Preferred" className="w-6 h-4 object-contain mr-2" />
                    <span className="text-sm text-gray-700">Preferred ($)</span>
                  </div>
                  <div className="flex items-center">
                    <img src="https://www.aa.com/content/images/seatMap/seats/seat-available@2x.png" alt="Available" className="w-6 h-4 object-contain mr-2" />
                    <span className="text-sm text-gray-700">Available</span>
                  </div>
                  <div className="flex items-center">
                    <img src="https://www.aa.com/content/images/seatMap/seats/seat-unavailable@2x.png" alt="Unavailable" className="w-6 h-4 object-contain mr-2" />
                    <span className="text-sm text-gray-700">Unavailable</span>
                  </div>
                  <div className="flex items-center">
                    <img src="https://www.aa.com/content/images/seatMap/seats/seat-qblock@2x.png" alt="Status Member" className="w-6 h-4 object-contain mr-2" />
                    <span className="text-sm text-gray-700">For status members</span>
                  </div>
                </div>
              </div>

              {/* Seat Map Container */}
              <section className="seat-map-container">
                <div className="seat-map-grid aircraft-E75">
                  
                  {/* Fuselage Body - Front Service Areas */}
                  <div className="fuselage body row-front flex items-center justify-center mb-4" style={{
                    borderLeft: '10px solid #D0DAE0',
                    borderRight: '10px solid #D0DAE0',
                    paddingLeft: '6px',
                    paddingRight: '6px',
                    maxWidth: '340px',
                    margin: '0 auto'
                  }}>
                    <div className="plane-body span0-5 w-4 flex items-center justify-center">
                      <div className="img-wrapper aisleNumber">
                        <img 
                          src="https://www.aa.com/content/images/seatMap/seats/exitrow-blank-80x160.png" 
                          alt=""
                          className="seat-exitrow w-4 h-8 object-contain"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    
                    <div className="span7 row-class flex items-center justify-center">
                      <div className="row-content flex items-center space-x-1">
                        <div className="span3 lavatory">
                          <div className="img-wrapper flex items-center justify-center w-24 h-10 bg-gray-300 rounded">
                            <img 
                              src="https://www.aa.com/content/images/seatMap/seat-lavatory@2x.png"
                              alt="Washroom"
                              className="w-12 h-8"
                            />
                          </div>
                        </div>
                        
                        <div className="span1 aisle">
                          <div className="firstBusinessRow img-wrapper flex items-center justify-center w-4 h-10">
                            <img 
                              src="https://www.aa.com/content/images/seatMap/seat-blank@2x.png"
                              alt=""
                              className="w-12 h-8"
                            />
                          </div>
                        </div>
                        
                        <div className="span3 galley">
                          <div className="img-wrapper flex items-center justify-center w-24 h-10 bg-gray-300 rounded">
                            <img 
                              src="https://www.aa.com/content/images/seatMap/seat-galley@2x.png"
                              alt="Flight attendant station"
                              className="w-6 h-6"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="plane-body span0-5 w-4 flex items-center justify-center">
                      <div className="img-wrapper aisleNumber">
                        <img 
                          src="https://www.aa.com/content/images/seatMap/seats/exitrow-blank-80x160.png" 
                          alt=""
                          className="seat-exitrow w-4 h-8 object-contain"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Seat Sections */}
                  {seatMap.sections.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="section mb-0">
                      <h4 
                        className="text-sm font-medium text-gray-700 mb-3 text-center"
                        style={{
                          borderLeft: '10px solid #D0DAE0',
                          borderRight: '10px solid #D0DAE0',
                          paddingLeft: '11px',
                          paddingRight: '11px',
                          maxWidth: '340px',
                          margin: '0 auto 0 auto',
                          paddingTop: '8px',
                          paddingBottom: '8px'
                        }}
                      >
                        {section.name}
                      </h4>
                      
                      {/* Seat Letter Headers aligned with seats */}
                      <div className="seat-letter-headers mb-2" style={{
                        borderLeft: '10px solid #D0DAE0',
                        borderRight: '10px solid #D0DAE0',
                        paddingLeft: '6px',
                        paddingRight: '6px',
                        maxWidth: '340px',
                        margin: '0 auto'
                      }}>
                        <div className="flex items-center justify-center">
                          <div className="flex items-center">
                            <div className="w-8 text-center text-xs text-gray-500 font-medium">A</div>
                            <div className="w-8 text-center text-xs text-gray-500 font-medium">B</div>
                            <div className="w-8 text-center text-xs text-gray-500"></div>
                            <div className="w-8 text-center text-xs text-gray-500 font-medium">D</div>
                            <div className="w-8 text-center text-xs text-gray-500 font-medium">F</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Bulkhead underline */}
                      <div className="bulkhead-underline mb-2" style={{
                        borderLeft: '10px solid #D0DAE0',
                        borderRight: '10px solid #D0DAE0',
                        paddingLeft: '6px',
                        paddingRight: '6px',
                        maxWidth: '340px',
                        margin: '0 auto'
                      }}>
                        <div className="flex items-center justify-center">
                          <div className="flex items-center space-x-0">
                            {/* Continuous underline under A, B */}
                            <img 
                              src="https://www.aa.com/content/images/seatMap/seat-bulkhead@2x.png"
                              alt="Bulkhead underline AB"
                              className="object-cover"
                              style={{width: '64px', height: '3px'}}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'block';
                              }}
                            />
                            <div 
                              className="bg-gray-500"
                              style={{width: '64px', height: '2px', display: 'none'}}
                            />
                            {/* Empty space for center */}
                            <div className="w-8"></div>
                            {/* Continuous underline under D, F */}
                            <img 
                              src="https://www.aa.com/content/images/seatMap/seat-bulkhead@2x.png"
                              alt="Bulkhead underline DF"
                              className="object-cover"
                              style={{width: '64px', height: '3px'}}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'block';
                              }}
                            />
                            <div 
                              className="bg-gray-500"
                              style={{width: '64px', height: '2px', display: 'none'}}
                            />
                          </div>
                        </div>
                      </div>
                      
                      
                      {section.rows.map((row, rowIndex) => {
                        const isExitRow = row.number === 17 || row.number === 18;
                        const isGapRow = row.number >= 17 && row.number <= 19; // Rows 17-19 have fuselage gap
                        const isLastBeforeGap = row.number === 16; // Row 16 is the last with fuselage before gap
                        const isFirstAfterGap = row.number === 20; // Row 20 is the first with fuselage after gap
                        
                        return (
                          <div key={rowIndex}>
                            <div 
                              className={`row flex items-center justify-center mb-1 ${isExitRow ? 'exit-row relative' : ''}`}
                              style={{
                                borderLeft: isGapRow ? 'none' : '10px solid #D0DAE0',
                                borderRight: isGapRow ? 'none' : '10px solid #D0DAE0',
                                borderBottomLeftRadius: isLastBeforeGap ? '16px' : '0',
                                borderBottomRightRadius: isLastBeforeGap ? '16px' : '0',
                                borderTopLeftRadius: isFirstAfterGap ? '16px' : '0',
                                borderTopRightRadius: isFirstAfterGap ? '16px' : '0',
                                paddingLeft: '6px',
                                paddingRight: '6px',
                                maxWidth: '340px',
                                margin: '0 auto'
                              }}
                            >
                            {/* Left exit row indicator */}
                            {isExitRow && (
                              <div className="plane-body row-number w-4 flex items-center justify-center">
                                <div className="img-wrapper aisleNumber">
                                  <img 
                                    src="https://www.aa.com/content/images/seatMap/seats/exitrow-left-80x160.png" 
                                    alt="Exit row left"
                                    className="seat-exitrow w-4 h-8 object-contain"
                                    aria-hidden="true"
                                  />
                                </div>
                              </div>
                            )}
                            
                            <div className="row-class flex items-center space-x-0">
                              <div className="mce exit row-content flex items-center">
                                {row.seats.map((seat, seatIndex) => (
                                  seat === "" ? (
                                    <div key={seatIndex} className="span1 w-8 h-8 relative">
                                      <div className="seat-bg relative w-8 h-8">
                                        {isExitRow && (
                                          <img 
                                            src="https://www.aa.com/content/images/seatMap/seats/seat-exit@2x.png"
                                            alt="Exit row background"
                                            className="seat-exit absolute inset-0 w-8 h-8 object-cover"
                                          />
                                        )}
                                        <div className="exit-seat-wrapper relative z-10">
                                          <div className="aisleNumber flex items-center justify-center h-full">
                                            <div className="seatID text-xs text-gray-500 font-bold" aria-hidden="true">
                                              {row.number}
                                            </div>
                                          </div>
                                          {isExitRow && (
                                            <img 
                                              src="https://www.aa.com/content/images/seatMap/seat-blank@2x.png" 
                                              alt=""
                                              className="seat seat-img absolute inset-0 w-8 h-8 object-contain opacity-0"
                                            />
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div key={seat} className="span1 w-8 h-8 relative">
                                      <div className="seat-bg relative w-8 h-8">
                                        {isExitRow && (
                                          <img 
                                            src="https://www.aa.com/content/images/seatMap/seats/seat-exit@2x.png"
                                            alt="Exit row background"
                                            className="seat-exit absolute inset-0 w-8 h-8 object-cover z-0"
                                          />
                                        )}
                                        
                                        <div className={`seat-wrapper ${getSeatStatus(seat) === 'available' ? 'is-available' : ''} ${getSeatStatus(seat) === 'mce' ? 'mce-seat mce' : ''} ${isExitRow ? 'exit' : ''} relative z-10 w-6 h-6`} style={{top: '4px', left: '4px'}}>
                                          {failedImages.has(getSeatImage(seat)) ? (
                                            <div className={getFallbackSeatStyles(seat)}>
                                              {seat.slice(-1)}
                                            </div>
                                          ) : (
                                            <img 
                                              src={getSeatImage(seat)} 
                                              alt={`Seat ${seat}`}
                                              className="w-6 h-6 object-contain"
                                              onError={() => handleImageError(getSeatImage(seat))}
                                            />
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )
                                ))}
                              </div>
                            </div>
                            
                            {/* Right exit row indicator */}
                            {isExitRow && (
                              <div className="plane-body row-number w-4 flex items-center justify-center">
                                <div className="img-wrapper aisleNumber">
                                  <img 
                                    src="https://www.aa.com/content/images/seatMap/seats/exitrow-right-80x160.png" 
                                    alt="Exit row right"
                                    className="seat-exitrow w-4 h-8 object-contain"
                                    aria-hidden="true"
                                  />
                                </div>
                              </div>
                            )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}

                  {/* Rear Service Areas */}
                  <div className="service-areas" style={{
                    borderLeft: '10px solid #D0DAE0',
                    borderRight: '10px solid #D0DAE0',
                    paddingLeft: '6px',
                    paddingRight: '6px',
                    maxWidth: '340px',
                    margin: '0 auto',
                    paddingTop: '8px'
                  }}>
                    <div className="flex justify-center mb-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-16 h-8 bg-slate-300 rounded flex items-center justify-center">
                          <img src="https://www.aa.com/content/images/seatMap/seat-lavatory@2x.png" alt="Washroom" className="w-6 h-6" />
                        </div>
                        <div className="w-4"></div>
                        <div className="w-16 h-8 bg-gray-300 rounded flex items-center justify-center">
                          <img src="https://www.aa.com/content/images/seatMap/seat-galley@2x.png" alt="Flight attendant station" className="w-6 h-6" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6  bg-white flex-shrink-0">
          <div></div>
          <button
            onClick={onClose}
            className="px-8 py-3 bg-aa-dark-blue text-white rounded-lg hover:bg-aa-dark-blue/90 transition-colors font-medium text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SeatSelectionDialog;